import {
  Accordion as ReachAccordion,
  AccordionButton as ReachAccordionButton,
  AccordionItem as ReachAccordionItem,
  AccordionPanel as ReachAccordionPanel,
  useAccordionItemContext,
} from '@reach/accordion';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import '@reach/accordion/styles.css';

import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { classNames } from '@modules/classname-helpers';
import { motion } from 'motion/react';

import styles from './Accordion.module.scss';

const UncontrolledAccordion: FC<
  PropsWithChildren<{
    collapsible?: boolean;
    defaultIndex?: number | number[];
    multiple?: boolean;
    onChange?: (value: number) => void;
    children: ReactNode;
  }>
> = ({ children, collapsible = true, defaultIndex, multiple = true, onChange }) => {
  return (
    <ReachAccordion
      className={styles['accordion']}
      collapsible={collapsible}
      defaultIndex={defaultIndex}
      multiple={multiple}
      onChange={onChange}
    >
      {children}
    </ReachAccordion>
  );
};

const ControlledAccordion: FC<
  PropsWithChildren<{
    index: number | number[];
    onChange: (value: number) => void;
    children: ReactNode;
  }>
> = ({ children, index, onChange }) => {
  return (
    <ReachAccordion index={index} onChange={onChange} className={styles['accordion']}>
      {children}
    </ReachAccordion>
  );
};

const AccordionItem: FC<
  PropsWithChildren<{
    disabled?: boolean;
    children: ReactNode;
    className?: string;
  }>
> = ({ children, disabled = false, className }) => {
  return (
    <ReachAccordionItem disabled={disabled} className={className}>
      {children}
    </ReachAccordionItem>
  );
};

const AccordionButton: FC<
  PropsWithChildren<{
    children: ReactNode;
    headingLevel: 1 | 2 | 3 | 4 | 5;
    className?: string;
  }>
> = ({ children, headingLevel = 3, className }) => {
  const Comp = `h${headingLevel}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
  return (
    <Comp>
      <ReachAccordionButton className={className}>{children}</ReachAccordionButton>
    </Comp>
  );
};

const AccordionSimpleHeader: FC<
  PropsWithChildren<{
    children: ReactNode;
    headingLevel?: 1 | 2 | 3 | 4 | 5;
    className?: string;
  }>
> = ({ children, headingLevel = 3, className }) => {
  const { isExpanded } = useAccordionItemContext();

  return (
    <div className={classNames(styles['accordion-header'], className)}>
      <AccordionButton headingLevel={headingLevel}>
        {children}
        <motion.span className={styles['accordion-header-icon']} data-is-expanded={isExpanded}>
          <FontAwesomeIcon icon={faChevronRight} size="sm" />
        </motion.span>
      </AccordionButton>
    </div>
  );
};

const AccordionPanel: FC<
  PropsWithChildren<{
    children: ReactNode;
  }>
> = ({ children }) => {
  const { isExpanded } = useAccordionItemContext();

  return (
    <ReachAccordionPanel>
      <motion.div animate={{ opacity: isExpanded ? 1 : 0 }} transition={{ duration: 0.5 }}>
        {children}
      </motion.div>
    </ReachAccordionPanel>
  );
};

export {
  UncontrolledAccordion,
  ControlledAccordion,
  AccordionItem,
  AccordionButton,
  AccordionSimpleHeader,
  AccordionPanel,
};
